import type { ReportData, ReportSection, CompanyInfo, ReportMetadata } from './types';
import { buildZeitangabenHtml } from './buildZeitangabenHtml';

interface CollectParams {
  formData: Record<string, string>;
  selectedCustomer: any;
  section1CardTexts: Record<string, { aiDraft: string; finalText: string; showAiDraft: boolean; saved: boolean }>;
  section2CardTexts?: Record<string, { aiDraft: string; finalText: string; showAiDraft: boolean; saved: boolean }>;
  section4CardTexts?: Record<string, { aiDraft: string; finalText: string; showAiDraft: boolean; saved: boolean }>;
  sectionStates: Record<string, { aiDraft: string; finalText: string; showAiDraft: boolean; saved: boolean }>;
  sectionDataRef: Record<string, any>;
  consultingDays: Array<{ date: string; hours: string }>;
  consultants: Array<{ name: string }>;
  advisorNumber?: string;
}

const SECTION_KEYS_ORDERED = [
  { key: 'analysis', number: 2, title: '2. Analyse der Unternehmenssituation unter Beachtung des Beratungsauftrages und der mit der Beratung verbundenen Zielsetzung' },
  { key: 'execution', number: 3, title: '3. Angaben zur Auftragsdurchführung und Erläuterung des Vorgehens' },
  { key: 'weaknesses', number: 4, title: '4. Benennung der einzelnen ermittelten Schwachstellen und Ursachen des Beratungsbedarfs' },
  { key: 'action-plan', number: 5, title: '5. Darstellung des detaillierten Maßnahmeplans zur Überwindung der ermittelten Schwachstellen' },
  { key: 'principles', number: 6, title: '6. Berücksichtigung der bereichsübergreifenden Grundsätze der Gleichstellung, Antidiskriminierung und ökologischen Nachhaltigkeit' },
  { key: 'confirmation', number: 7, title: '7. Bestätigung der Beratungsleistung und Eigenständigkeit der Berichterstellung' },
];

export function collectReportData(params: CollectParams): ReportData {
  const { formData, selectedCustomer, section1CardTexts, section2CardTexts, section4CardTexts, sectionStates, sectionDataRef, consultingDays, consultants, advisorNumber } = params;

  const get = (formKey: string, custKey?: string): string =>
    formData[formKey] || (selectedCustomer && custKey ? String(selectedCustomer[custKey] || '') : '') || '';

  // Kunden-Telefon: neuere Kunden pflegen Vorwahl + Rufnummer getrennt (phoneVorwahl/
  // phoneNummer) — dann ist das alte Einzelfeld `phone` leer. Beide zusammensetzen,
  // sonst auf das Alt-Feld zurückfallen (wie BafaFormsService.customerPhone).
  const getTelefon = (): string => {
    if (formData['telefon']) return formData['telefon'];
    if (!selectedCustomer) return '';
    const combined = [selectedCustomer.phoneVorwahl, selectedCustomer.phoneNummer]
      .map((s: string) => (s || '').trim())
      .filter(Boolean)
      .join(' ');
    return combined || String(selectedCustomer.phone || '').trim();
  };

  const metadata: ReportMetadata = {
    aktenzeichen: formData['aktenzeichen'] || '',
    berichtstitel: formData['berichtstitel'] || 'BAFA Beratungsbericht',
    startDate: formData['startDate'] || '',
    endDate: formData['endDate'] || '',
  };

  const company: CompanyInfo = {
    name: get('org-name', 'name'),
    rechtsform: get('rechtsform', 'rechtsform'),
    street: get('street', 'address'),
    plz: get('plz', 'plz'),
    ort: get('ort', 'ort'),
    land: get('land'),
    website: get('website', 'website'),
    anrede: get('anrede', 'anrede'),
    vorname: get('vorname', 'vorname'),
    nachname: get('nachname', 'nachname'),
    telefon: getTelefon(),
    email: get('email', 'email'),
    mitarbeiterzahl: get('mitarbeiterzahl', 'mitarbeiterzahl'),
    gruendung: get('gruendung', 'gruendung'),
    bilanzsumme: get('bilanzsumme', 'jahresbilanzsumme'),
    jahresumsatz: get('jahresumsatz', 'jahresumsatz'),
    wirtschaftszweig: get('wirtschaftszweig', 'wirtschaftszweig'),
    geschaeftsgegenstand: get('geschaeftsgegenstand', 'geschaeftsgegenstand'),
  };

  // Build Section 1 from the 5 cards.
  // 1.5 (Zeitangaben) wird IMMER live aus den aktuellen Beratungstagen gerendert —
  // nicht aus dem (potenziell veralteten) eingefrorenen finalText. So stimmt der
  // Beratungszeitraum überall automatisch und friert nie wieder ein.
  const cardKeys = ['card-1-1', 'card-1-2', 'card-1-3', 'card-1-4', 'card-1-5'];
  const section1Html = cardKeys
    .map(key => key === 'card-1-5'
      ? buildZeitangabenHtml(consultingDays, consultants)
      : (section1CardTexts[key]?.finalText || ''))
    .filter(html => html.trim().length > 0)
    .join('\n');

  const section1: ReportSection = {
    number: 1,
    title: '1. Darstellung des beratenen Unternehmens und Benennung des Beratungsauftrags',
    htmlContent: section1Html,
  };

  // Build Sections 2-7
  // Sections 6 (principles) and 7 (confirmation) store their content in sectionDataRef
  // instead of sectionStates.finalText, because they don't have a separate "final text" editor.
  const SECTION_DATA_REF_KEYS: Record<string, string> = {
    'principles': 'principlesText',
    'confirmation': 'confirmationText',
  };

  // Card-based sections: combine card texts into one HTML block
  const CARD_SECTIONS: Record<string, { cardTexts?: Record<string, any>; cardIds: string[] }> = {
    'analysis': { cardTexts: section2CardTexts, cardIds: ['card-2-1', 'card-2-2', 'card-2-3'] },
    'weaknesses': { cardTexts: section4CardTexts, cardIds: ['card-4-1', 'card-4-2'] },
  };

  const otherSections: ReportSection[] = SECTION_KEYS_ORDERED.map(({ key, number, title }) => {
    let htmlContent = '';

    // Card-based sections: combine card texts
    if (CARD_SECTIONS[key] && CARD_SECTIONS[key].cardTexts) {
      const { cardTexts, cardIds } = CARD_SECTIONS[key];
      htmlContent = cardIds
        .map(cardId => cardTexts?.[cardId]?.finalText || '')
        .filter(text => text.trim())
        .join('\n');
    }

    // Fallback to sectionStates.finalText (for old data or non-card sections)
    if (!htmlContent) {
      htmlContent = sectionStates[key]?.finalText || '';
    }

    // For sections that store content in sectionDataRef (6+7), use that as fallback
    if (!htmlContent && SECTION_DATA_REF_KEYS[key] && sectionDataRef[key]) {
      htmlContent = sectionDataRef[key][SECTION_DATA_REF_KEYS[key]] || '';
    }

    return { number, title, htmlContent };
  });

  return {
    metadata,
    company,
    consultants: consultants.filter(c => c.name.trim()),
    advisorNumber: (advisorNumber || '').trim() || undefined,
    consultingDays: consultingDays.filter(d => d.date),
    sections: [section1, ...otherSections],
  };
}
